"use client"
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Button,
  Container,
  Grid,
} from '@mui/material';

import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';

import { LFDsActiveFilters } from './LFDsActiveFilters';

// Theme Stuff / Theme Configs
import { getMuiTableBodyProps, getTableTheme } from '../../js/table_theme_config'; // Import the helpers

// Pure JS
import config from '../../js/config';
import downloadFile from '../../js/download_file';
import query_data from '../../js/query_data';

// Component Imports
import CreateDatasetDialog from '../dataset/CreateDatasetDialog';
import LFDsFilterDialog from './LFDsFilterDialog';
import GenericFolderUpload from '../../components/upload/GenericFolderUpload';
import { convertEpochToDateTime, formatDuration } from '../../js/time_helpers';
import ReviewPIIModal from './ReviewPIIModal';
import { useSearchParams } from 'react-router';




const LFDsTable = () => {
  // Data State & Table States
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);

  // Pagination state
  const [page, setPage] = useState(0); // 0-based for MRT
  const [pageSize, setPageSize] = useState(20);

  // LFDs Filter Dialog / URL Search Params
  const [isLFDsFilterDialogOpen, setisLFDsFilterDialogOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams({
    robot: [],
    operator: [],
    run_id: [],
    task_id: [],
    topics: [],
    session_log_type: '',
    task_successful: '',
    pii_reviewed: '',
    pii_approved: '',
    data_transferred: '',
  });

  // Create Dataset Dialog State Vars
  const [isCreateDatasetDialogOpen, setIsCreateDatasetDialogOpen] = useState<boolean>(false);

  // Review PII Modal State Vars
  const [reviewPIIModalOpen, setReviewPIIModalOpen] = useState<boolean>(false);
  const [videoCameras, setVideoCameras] = useState<string[]>([]);
  const [video_bucket_name_file_path, setVideoMinioBucketNameFile] = useState<string>('');
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);

  // Convert URLSearchParams to JSON for API
  function convertParamsToJSON(params) {
    const sessionLogType = params.get('session_log_type');
    return {
      robot: params.getAll('robot'),
      operator: params.getAll('operator'),
      run_id: params.getAll('run_id'),
      task_id: params.getAll('task_id'),
      topics: params.getAll('topics'),
      session_log_type: (sessionLogType && sessionLogType !== '') ? sessionLogType : undefined,
      task_successful: params.get('task_successful') === 'true' ? true : params.get('task_successful') === 'false' ? false : undefined,
      pii_reviewed: params.get('pii_reviewed') === 'true' ? true : params.get('pii_reviewed') === 'false' ? false : undefined,
      pii_approved: params.get('pii_approved') === 'true' ? true : params.get('pii_approved') === 'false' ? false : undefined,
      data_transferred: params.get('data_transferred') === 'true' ? true : params.get('data_transferred') === 'false' ? false : undefined,
    };
  }

  // Helper function to extract folder path from bucket_name_file_path
  const getFolderPath = (filePath) => {
    if (!filePath) return '';
    const parts = filePath.split('/');
    // Remove the filename part and join back
    return parts.slice(0, -1).join('/');
  };

  // Helper function to group data by folder
  const groupDataByFolder = (rawData) => {
    const grouped = {};
    
    rawData.forEach(item => {
      const folderPath = getFolderPath(item.bucket_name_file_path);
      
      if (!grouped[folderPath]) {
        // Create a new group with the first item's data
        grouped[folderPath] = {
          ...item,
          folderPath: folderPath,
          shards: [item],
          isGroup: true,
        };
      } else {
        // Add to existing group
        grouped[folderPath].shards.push(item);
        // Combine camera names and remove duplicates
        const existingCameras = grouped[folderPath].camera_names || [];
        const newCameras = item.camera_names || [];
        const allCameras = [...existingCameras, ...newCameras];
        grouped[folderPath].camera_names = Array.from(new Set(allCameras));
      }
    });

    return Object.values(grouped);
  };

  // Fetch and flatten data with pagination
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const jsonParams = convertParamsToJSON(searchParams);
      const params = { ...jsonParams, page: page + 1, size: pageSize };
      const json_list = await query_data("/lfds", params);
      
      // Group the data by folder
      const groupedData = groupDataByFolder(json_list.data);
      
      setData(groupedData);
      setTotal(json_list.total || 0);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }, [page, pageSize, searchParams]);

  const onSaveCallback = (requestBody) => {
    // Remove empty, null, undefined, or empty array values
    const cleaned = Object.fromEntries(
      Object.entries(requestBody).filter(([k, v]) => {
        if (Array.isArray(v)) return v.length > 0;
        return v !== '' && v !== null && v !== undefined;
      })
    );
    // Convert all values to string or string[]
    const stringified = Object.fromEntries(
      Object.entries(cleaned).map(([k, v]) => {
        if (Array.isArray(v)) return [k, v.map(String)];
        return [k, String(v)];
      })
    );

    setSearchParams(stringified);
    setPage(0); // Reset to first page on filter
    setisLFDsFilterDialogOpen(false);
  };

  const handleDownloadSelected = async (selectedRowIds) => {
    selectedRowIds.forEach(async (rowId) => {
      // Find the row data
      const rowData = data.find(item => 
        (item.isGroup && item.folderPath === rowId) || 
        item.bucket_name_file_path === rowId
      );
      
      if (rowData && rowData.isGroup && rowData.shards) {
        // Download all shards in the group
        rowData.shards.forEach(async (shard) => {
          await downloadFile(shard.bucket_name_file_path);
        });
      } else if (rowData) {
        // Download single file
        await downloadFile(rowData.bucket_name_file_path);
      }
    });
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Flat columns
  const columns = useMemo(
    () => [
      {
        id: 'start_time',
        header: 'Start Time',
        size: 40,
        Cell: ({ row }) => {
          const startTime = row.original.start_time;
          let date = '', time = '';
          if (startTime) {
            const dt = convertEpochToDateTime(startTime);
            date = dt.date;
            time = dt.time;
          }
          return (
            <div>
              <div>{date}</div>
              <div>{time}</div>
            </div>
          );
        },
      },
      {
        id: 'duration',
        header: 'Duration',
        size: 80,
        accessorFn: row => row.duration_s,
        Cell: ({ cell }) => formatDuration(cell.getValue()),
      },
      { accessorKey: 'robot', header: 'Robot', size: 40 },
      { accessorKey: 'operator', header: 'Operator', size: 40 },
      { accessorKey: 'task_id', header: 'Task Id', size: 140 },
      {
        accessorKey: 'filename',
        header: 'File Name / Folder',
        size: 340,
        Cell: ({ row }) => {
          const data = row.original;
          
          if (data.isSubRow) {
            // For sub-rows (individual shards), show just the filename with indentation
            const path = data.bucket_name_file_path;
            if (typeof path === 'string') {
              const parts = path.split('/');
              const filename = parts[parts.length - 1];
              return (
                <div style={{ paddingLeft: '20px', fontSize: '0.9em' }}>
                  📄 {filename}
                </div>
              );
            }
            return '';
          } else if (data.isGroup && data.shards) {
            // For grouped data, show folder path and shard count
            const folderName = data.folderPath.split('/').pop() || data.folderPath;
            return (
              <div>
                <div style={{ fontWeight: 'bold' }}>📁 {folderName}</div>
                <div style={{ fontSize: '0.8em', color: 'gray' }}>
                  {data.shards.length} shard{data.shards.length !== 1 ? 's' : ''}
                </div>
                <div style={{ fontSize: '0.7em', color: 'gray' }}>
                  {data.folderPath}
                </div>
              </div>
            );
          } else {
            // For individual files, show filename as before
            const path = data.bucket_name_file_path;
            if (typeof path === 'string') {
              const parts = path.split('/');
              return parts[parts.length - 1];
            }
            return '';
          }
        },
      },
      { accessorKey: 'task_successful', header: 'Success', size: 40, Cell: ({ cell }) => (cell.getValue() ? '✔️' : '❌') },
      {
        id: 'pii_status',
        header: 'PII',
        size: 100,
        Cell: ({ row }) => {
          const found = row.original.pii_found;
          const reviewed = row.original.pii_reviewed;
          const approved = row.original.pii_approved;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span>Found: {found ? '✔️' : '❌'}</span>
              <span>Reviewed: {reviewed ? '✔️' : '❌'}</span>
              <span>Approved: {approved ? '✔️' : '❌'}</span>
            </div>
          );
        },
      },

      {
        accessorKey: 'review',
        header: 'Review',
        size: 80,
        Cell: ({ cell }) => {
          const rowData = cell.row.original;
          
          // Don't show review button for sub-rows
          if (rowData.isSubRow) return null;
          
          const hasCamera = rowData.camera_names && rowData.camera_names.length > 0;
          
          if (!hasCamera) return null;
          
          // Get the row ID for highlighting
          const rowId = rowData.isGroup && rowData.folderPath
            ? rowData.folderPath
            : rowData.bucket_name_file_path;

          return (
            <Button onClick={() => {
              setReviewPIIModalOpen(true);
              setVideoCameras(rowData.camera_names);
              setHighlightedRowId(rowId);

              // For grouped data, use the first shard's bucket_name_file_path
              if (rowData.isGroup && rowData.shards && rowData.shards.length > 0) {
                setVideoMinioBucketNameFile(rowData.shards[0].bucket_name_file_path);
              } else {
                setVideoMinioBucketNameFile(rowData.bucket_name_file_path);
              }
            }}>Review</Button>
          );
        }
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: true,
    enableStickyHeader: true,
    enableExpanding: true,
    getSubRows: (row) => {
      // Return shards as sub-rows if this is a grouped row
      if (row.isGroup && row.shards && row.shards.length > 1) {
        return row.shards.map(shard => ({
          ...shard,
          isSubRow: true,
        }));
      }
      return undefined;
    },
    state: { isLoading: loading, pagination: { pageIndex: page, pageSize } },
    manualPagination: true,
    rowCount: total,
    muiPaginationProps: {
      rowsPerPageOptions: [10, 20, 50, 100],
    },
    onPaginationChange: (updater) => {
      // updater can be a function or value
      const next = typeof updater === 'function' ? updater({ pageIndex: page, pageSize }) : updater;
      if (next.pageSize !== pageSize) {
        setPageSize(next.pageSize);
        setPage(0); // Reset to first page if page size changes
      } else {
        setPage(next.pageIndex);
      }
    },
    muiTableBodyProps: {
      ...getMuiTableBodyProps(false),
      sx: { '& td': { padding: '0.5rem' } },
    },
    muiTableBodyRowProps: ({ row }) => {
      const rowId = row.original.isGroup && row.original.folderPath
        ? row.original.folderPath
        : row.original.bucket_name_file_path;
      const isHighlighted = highlightedRowId === rowId;
      return {
        sx: isHighlighted ? {
          backgroundColor: 'rgba(25, 118, 210, 0.12)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.2)',
          },
        } : {},
      };
    },
    muiTableHeadCellProps: {
      sx: { padding: '0.5rem' },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 300px)',
        overflowY: 'auto',
      },
    },
    mrtTheme: getTableTheme(false),
    positionToolbarAlertBanner: 'bottom',
    getRowId: (row) => {
      // For sub-rows, use their bucket_name_file_path
      if (row.isSubRow) {
        return row.bucket_name_file_path || row.filename;
      }
      // For grouped data, use folder path as ID
      if (row.isGroup && row.folderPath) {
        return row.folderPath;
      }
      // For individual files, use bucket_name_file_path
      return row.bucket_name_file_path || row.filename;
    },
    renderTopToolbarCustomActions: ({ table }) => {
      const selection = table.getState().rowSelection;
      const selected = Object.keys(selection).filter((key) => selection[key]);
      return (
        <Grid container justifyContent="flex-start">
          <Button onClick={fetchData}>Refresh</Button>
          <Button onClick={() => setisLFDsFilterDialogOpen(true)}>Set Filters</Button>
          <GenericFolderUpload data_type="LFD" buttonText="Upload LFDS" />
          {selected.length > 0 && (
            <>
              <Button onClick={() => { handleDownloadSelected(selected); }}>Download LFDs</Button>
              <Button onClick={() => setIsCreateDatasetDialogOpen(true)}>Create Dataset</Button>
            </>
          )}
        </Grid>
      );
    },
  });

  return (
    <Container maxWidth={false} disableGutters sx={{ pt: 2, width: '100%' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={8} container spacing={2} alignItems="center">
          <LFDsFilterDialog
            open={isLFDsFilterDialogOpen}
            onClose={() => setisLFDsFilterDialogOpen(false)}
            onSaveCallback={onSaveCallback}
            searchParams={searchParams}
          />
          <CreateDatasetDialog
            open={isCreateDatasetDialogOpen}
            onClose={() => setIsCreateDatasetDialogOpen(false)}
            minioBucketFilePaths={(() => {
              const selectedRowIds = Object.keys(table.getState().rowSelection).filter(path => !path.startsWith('GROUPING_ROW'));
              const allPaths = [];
              
              selectedRowIds.forEach(rowId => {
                const rowData = data.find(item => 
                  (item.isGroup && item.folderPath === rowId) || 
                  item.bucket_name_file_path === rowId
                );
                
                if (rowData && rowData.isGroup && rowData.shards) {
                  // Add all shard paths from the group
                  rowData.shards.forEach(shard => {
                    allPaths.push(shard.bucket_name_file_path);
                  });
                } else if (rowData) {
                  // Add single file path
                  allPaths.push(rowData.bucket_name_file_path);
                }
              });
              
              return allPaths;
            })()}
          />
        </Grid>
      </Grid>
      <MaterialReactTable table={table} />
      <ReviewPIIModal open={reviewPIIModalOpen} onClose={() => setReviewPIIModalOpen(false)} cameras={videoCameras} bucket_name_file_path={video_bucket_name_file_path} />
    </Container>
  );
};

export default LFDsTable;